/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { type BackgroundColor, Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	root: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		gap: token('space.200'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		paddingBlockStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		borderRadius: token('radius.large'),
		'@media (min-width: 48rem)': {
			gridTemplateColumns: '1fr 1fr',
		},
	},
	content: {
		width: '100%',
		height: '100%',
	},
	image: {
		width: '100%',
		maxWidth: 'fit-content',
	},
	tile: {
		height: '80px',
		minWidth: '80px',
		borderRadius: token('radius.large'),
	},
	list: {
		paddingInlineStart: token('space.250'),
	},
});

interface UICardProps {
	children: ReactNode;
}

export const UICard: ({ children }: UICardProps) => JSX.Element = ({ children }: UICardProps) => {
	return <Box xcss={styles.root}>{children}</Box>;
};

interface Data {
	color: BackgroundColor;
	heading: string;
	body: ReactNode;
}

const data: Data[] = [
	{
		color: 'color.background.accent.gray.subtler',
		heading: 'Platform Collection',
		body: (
			<ul css={styles.list}>
				<li>Apps: Home, Teams, Goals, Projects</li>
				<li>Background color: color/background/accent/gray/subtler/default</li>
			</ul>
		),
	},
	{
		heading: 'Teamwork Collection',
		color: 'color.background.accent.blue.subtlest.pressed',
		body: (
			<ul css={styles.list}>
				<li>Apps: Jira, Confluence, Loom</li>
				<li>Background color: color/background/accent/blue/subtlest/pressed</li>
			</ul>
		),
	},
	{
		heading: 'Software Collection',
		color: 'color.background.accent.lime.subtler.hovered',
		body: (
			<ul css={styles.list}>
				<li>Apps: Rovo Dev, DX, Pipelines, Bitbucket, Compass</li>
				<li>Background color: color/background/accent/lime/subtler/hovered</li>
			</ul>
		),
	},
	{
		heading: 'Strategy Collection',
		color: 'color.background.accent.orange.subtler.pressed',
		body: (
			<ul css={styles.list}>
				<li>Apps: Focus, Talent, Align</li>
				<li>Background color: color/background/accent/orange/subtler/pressed</li>
			</ul>
		),
	},
	{
		heading: 'Service Collection',
		color: 'color.background.accent.orange.subtlest.pressed',
		body: (
			<ul css={styles.list}>
				<li>Apps: Jira Service Management, Customer Service Management, Assets</li>
				<li>Background color: color/background/accent/orange/subtlest/pressed</li>
			</ul>
		),
	},
];

const Tile = ({ color }: { color: BackgroundColor }) => {
	return <Box backgroundColor={color} xcss={styles.tile}></Box>;
};

// TODO: Fill in the component {description} and ensure links point to the correct {packageName} location.
// Remove links that the component does not have (such as usage). If there are no links remove them all.
/**
 * __Spotlight collection background colors__
 *
 * A spotlight collection background colors {description}.
 *
 * - [Examples](https://atlassian.design/components/{packageName}/examples)
 * - [Code](https://atlassian.design/components/{packageName}/code)
 * - [Usage](https://atlassian.design/components/{packageName}/usage)
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports, @repo/internal/react/require-jsdoc
export const SpotlightCollectionBackgroundColors: () => JSX.Element = () => {
	return (
		<Stack space="space.100">
			{data.map(({ heading, body, color }) => (
				<Inline space="space.200">
					<Tile color={color} />
					<Stack>
						<Heading size="small">{heading}</Heading>
						{body}
					</Stack>
				</Inline>
			))}
		</Stack>
	);
};
